import React, { useEffect, useRef } from "react";
import { PlayerKeyboardControlsProps } from "./playerKeyboardTypes";
import type { FocusDirection } from "../tvNavigation/core";
import { moveWebFocus, setFocusModality } from "../tvNavigation/registry";
import { isTelevisionRuntime } from "../utils/platformCapabilities";

export function PlayerKeyboardControls(props: PlayerKeyboardControlsProps) {
  const callbacks = useRef(props);
  const wheelDelta = useRef(0);
  const wheelFrame = useRef<number | null>(null);
  callbacks.current = props;

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.tagName === "INPUT" || target?.tagName === "TEXTAREA") return;
      callbacks.current.onActivity();
      if (isTelevisionRuntime() && (event.key === " " || event.key.toLowerCase() === "k" || event.key === "Escape" || event.key.startsWith("Arrow"))) return;
      if (event.key === " " || event.key.toLowerCase() === "k") {
        event.preventDefault();
        callbacks.current.onTogglePlayback();
      } else if (event.key === "ArrowLeft" || event.key === "ArrowRight" || event.key === "ArrowUp" || event.key === "ArrowDown") {
        event.preventDefault();
        setFocusModality("keyboard");
        moveWebFocus(({ ArrowLeft: "left", ArrowRight: "right", ArrowUp: "up", ArrowDown: "down" } as Record<string, FocusDirection>)[event.key]);
      } else if (event.key.toLowerCase() === "m") {
        callbacks.current.onToggleMute();
      } else if (event.key.toLowerCase() === "f") {
        callbacks.current.onToggleFullscreen();
      } else if (event.key === "Escape") {
        event.preventDefault();
        callbacks.current.onDismiss();
      }
    };
    const handleWheel = (event: WheelEvent) => {
      if (document.visibilityState !== "visible" || !document.hasFocus()) return;
      const target = event.target as HTMLElement | null;
      if (target?.closest('input, textarea, select, [aria-label="Player menu"], [aria-label="Playback timeline"], [aria-label="Volume"]')) return;
      if (Math.abs(event.deltaY) < Math.abs(event.deltaX) || event.deltaY === 0) return;
      event.preventDefault();
      callbacks.current.onActivity();
      wheelDelta.current += event.deltaY;
      if (wheelFrame.current !== null) return;
      wheelFrame.current = window.requestAnimationFrame(() => {
        wheelFrame.current = null;
        const accumulated = wheelDelta.current;
        wheelDelta.current = 0;
        if (Math.abs(accumulated) < 4) return;
        callbacks.current.onAdjustVolume(accumulated < 0 ? 1 : -1);
      });
    };
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("wheel", handleWheel, { passive: false });
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("wheel", handleWheel);
      if (wheelFrame.current !== null) window.cancelAnimationFrame(wheelFrame.current);
    };
  }, []);

  return null;
}
